// src/supplier/pages/SupplierVerifyEmail.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supplierAuthAPI } from "../services/supplierApi";
import { Building2, Loader2, RotateCw } from "lucide-react";

const STORAGE_KEY = "supplier_pending_verification_email";

function normalizeEmail(v) {
  return String(v || "").trim().toLowerCase();
}
function readStoredEmail() {
  try { return sessionStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
}
function storeEmail(v) {
  try {
    const n = normalizeEmail(v);
    if (n) sessionStorage.setItem(STORAGE_KEY, n);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}
function clearStoredEmail() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}
function maskEmail(email) {
  const n = normalizeEmail(email);
  if (!n || !n.includes("@")) return "";
  const [local, domain] = n.split("@");
  return `${local.slice(0, 2)}${"*".repeat(Math.max(local.length - 2, 2))}@${domain}`;
}

export default function SupplierVerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();

  const routeVerification = location.state?.verification || null;
  const initialEmail = useMemo(() => {
    return normalizeEmail(
      location.state?.email || routeVerification?.email || readStoredEmail()
    );
  }, [location.state, routeVerification]);

  const [email] = useState(initialEmail);
  const [otpCode, setOtpCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const hasContext = Boolean(email);
  const masked = useMemo(() => maskEmail(email), [email]);

  const [cooldown, setCooldown] = useState(() => {
    const init = Number(location.state?.cooldownSeconds ?? routeVerification?.resendCooldownSeconds ?? 0);
    return Number.isFinite(init) && init > 0 ? init : 0;
  });

  const [notice, setNotice] = useState(() => {
    if (!initialEmail) {
      return { tone: "warning", text: "We could not restore your pending verification session. Please register again or try signing in." };
    }
    if (location.state?.deliveryStatus === "failed") {
      return { tone: "warning", text: "Your account was created, but we could not deliver the verification email. You can request a new code below." };
    }
    if (location.state?.fromLogin) {
      return { tone: "info", text: "Your email still needs verification before you can sign in." };
    }
    return null;
  });

  useEffect(() => { storeEmail(email); }, [email]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const normOtp = String(otpCode || "").replace(/\D/g, "").slice(0, 6);
    if (!normalizeEmail(email)) {
      setNotice({ tone: "error", text: "No verification context. Please register again or try signing in." });
      return;
    }
    if (normOtp.length !== 6) {
      setNotice({ tone: "error", text: "Enter the 6-digit verification code from your email." });
      return;
    }
    setSubmitting(true);
    setNotice(null);
    try {
      await supplierAuthAPI.verifyEmail({ email: normalizeEmail(email), otpCode: normOtp });
      clearStoredEmail();
      setNotice({ tone: "success", text: "Email verified! Redirecting to sign in…" });
      setTimeout(() => navigate("/supplier/login?verified=1", { replace: true }), 1200);
    } catch (error) {
      const payload = error?.response?.data || {};
      const retry = Number(payload?.errors?.retryAfterSeconds || 0);
      if (retry > 0) setCooldown(retry);

      if (payload?.code === "ACCOUNT_ALREADY_VERIFIED") {
        clearStoredEmail();
        setNotice({ tone: "info", text: "This account is already verified. Redirecting…" });
        setTimeout(() => navigate("/supplier/login?verified=1", { replace: true }), 1200);
        return;
      }
      setNotice({ tone: "error", text: payload?.message || error?.message || "Verification failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending || cooldown > 0) return;
    if (!normalizeEmail(email)) {
      setNotice({ tone: "error", text: "No verification context. Please register again." });
      return;
    }
    setResending(true);
    setNotice(null);
    try {
      const res = await supplierAuthAPI.resendOtp({ email: normalizeEmail(email) });
      const next = Number(res?.verification?.resendCooldownSeconds);
      setCooldown(Number.isFinite(next) && next > 0 ? next : 0);
      setNotice({
        tone: res?.delivery?.status === "failed" ? "warning" : "success",
        text: res?.delivery?.status === "failed"
          ? "Could not send the verification email. Please wait and try again."
          : "A fresh verification code has been sent to your email.",
      });
    } catch (error) {
      const payload = error?.response?.data || {};
      const retry = Number(payload?.errors?.retryAfterSeconds || 0);
      if (retry > 0) setCooldown(retry);
      setNotice({ tone: "error", text: payload?.message || error?.message || "Could not resend verification code." });
    } finally {
      setResending(false);
    }
  };

  const toneClasses = {
    info: "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200",
    warning: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200",
    error: "border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200",
  };

  return (
    <div className="supplier-portal flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-neutral-100 px-4 py-8 dark:from-neutral-900 dark:to-neutral-950">
      <div className="operations-modal-panel w-full max-w-md p-6 dark:border-neutral-700 dark:bg-neutral-900">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/50">
            <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Verify Your Email</h1>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Enter the 6-digit code we sent to your email to activate your supplier account.
          </p>
        </div>

        {/* Masked email */}
        {hasContext && (
          <div className="mb-4 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Verification email sent to</p>
            <p className="mt-0.5 text-sm font-medium text-neutral-900 dark:text-neutral-100">{masked}</p>
          </div>
        )}

        {/* Notice */}
        {notice && (
          <div className={`mb-4 rounded-lg border px-3 py-2.5 text-xs ${toneClasses[notice.tone] || toneClasses.info}`}>
            {notice.text}
          </div>
        )}

        {/* OTP form */}
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">
              Verification Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-center text-lg font-semibold tracking-[0.3em] outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
              placeholder="000000"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || otpCode.length !== 6}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Verifying…" : "Verify Email"}
          </button>
        </form>

        {/* Resend */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || cooldown > 0 || !hasContext}
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline disabled:cursor-not-allowed disabled:text-neutral-400 dark:text-blue-400 dark:disabled:text-neutral-600"
          >
            <RotateCw className={`h-3.5 w-3.5 ${resending ? "animate-spin" : ""}`} />
            {cooldown > 0 ? `Resend code in ${cooldown}s` : resending ? "Sending…" : "Resend code"}
          </button>
        </div>

        {/* Links */}
        <p className="mt-5 text-center text-xs text-neutral-500 dark:text-neutral-400">
          <Link to="/supplier/login" className="text-blue-600 hover:underline dark:text-blue-400">
            Back to sign in
          </Link>
          {" · "}
          <Link to="/supplier/register" className="text-blue-600 hover:underline dark:text-blue-400">
            Register new account
          </Link>
        </p>
      </div>
    </div>
  );
}
